import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, useRef } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { AuthProvider, API } from "@/auth/AuthContext";
import Landing from "@/components/Landing";
import ServicesPage from "@/pages/ServicesPage";
import AdminPage from "@/pages/AdminPage";
import AboutPage from "@/pages/AboutPage";
import CareersPage from "@/pages/CareersPage";
import LocalAIPage from "@/pages/LocalAIPage";
import SolarPage from "@/pages/SolarPage";
import BookingPage from "@/pages/BookingPage";
import TVMountingPage from "@/pages/TVMountingPage";
import DFWTVMountingPage from "@/pages/DFWTVMountingPage";
import PromotionsPage from "@/pages/PromotionsPage";

function AuthCallback() {
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    const finish = () => { window.location.href = location.pathname; };
    axios.post(`${API}/auth/google/session`, { session_id: sessionId }, { withCredentials: true })
      .then(({ data }) => {
        if (data.role === "admin") {
          localStorage.setItem("dt28_token", data.session_token);
          localStorage.setItem("dt28_admin_user", JSON.stringify(data));
          toast.success("Welcome back, admin.");
        } else {
          localStorage.setItem("dt28_customer", JSON.stringify(data));
          toast.success(`Signed in as ${data.name || data.email}.`);
        }
        finish();
      })
      .catch(() => {
        toast.error("Google sign-in failed. Please try again.");
        finish();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <p className="text-zinc-500 font-mono text-xs uppercase tracking-widest animate-pulse">Signing you in...</p>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Emergent OAuth returns session_id in the URL fragment — detect synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/careers-partners" element={<CareersPage />} />
      <Route path="/local-ai" element={<LocalAIPage />} />
      <Route path="/solar-installation" element={<SolarPage />} />
      <Route path="/book" element={<BookingPage />} />
      <Route path="/tv-mounting" element={<TVMountingPage />} />
      <Route path="/dfw-tv-mounting" element={<DFWTVMountingPage />} />
      <Route path="/promotions" element={<PromotionsPage />} />
      <Route path="/admin" element={<AdminPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="dir-1 min-h-screen bg-[#0a0a0c] text-[#e8e8ed] antialiased selection:bg-[#d4af37] selection:text-black">
      <AuthProvider>
        <HelmetProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </HelmetProvider>
      </AuthProvider>
      <Toaster position="bottom-center" theme="dark" />
    </div>
  );
}
